// Buy loons dialog component for the library page

import React from 'react';
import PricingTable from '@/components/products/PricingTable';
import { Spinner } from '@/components/ui/spinner';
import { useProductPaymentMethods } from '@/hooks';
import { useLoonProducts } from '@/transport/cache';
import ConfirmBuyLoons from '../loons/ConfirmBuyLoons';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { getProductLoonsGranted } from '@airhead-games/shared/utilities';
import type { LibraryDialogOutletProps } from './dialogOutletTypes';

const BuyLoonsDialog: React.FC<LibraryDialogOutletProps> = ({
  user,
  dialog,
  setDialog,
}) => {
  const isOpen = dialog === 'buy-loons';
  const shouldResolveQuickBuy = isOpen && !!user?.rapidReload;
  const {
    products,
    loading: productsLoading,
    isFetched: productsFetched,
  } = useLoonProducts({
    autoFetch: shouldResolveQuickBuy,
  });
  const {
    paymentMethods,
    hasSavedMethod,
    isFetched: paymentMethodsFetched,
  } = useProductPaymentMethods({
    autoFetch: shouldResolveQuickBuy,
  });

  const rapidReloadProduct = user?.rapidReload
    ? products.find((product) => product.stripePriceId === user.rapidReload) ||
      null
    : null;
  const showQuickBuyConfirm =
    !!user?.rapidReload && hasSavedMethod && !!rapidReloadProduct;
  const isResolvingQuickBuy =
    shouldResolveQuickBuy &&
    (productsLoading || !productsFetched || !paymentMethodsFetched);
  const quickBuyLoonCount = rapidReloadProduct
    ? getProductLoonsGranted(rapidReloadProduct)
    : null;

  if (!user) {
    return null;
  }

  if (showQuickBuyConfirm && rapidReloadProduct) {
    return (
      <ConfirmBuyLoons
        open={isOpen}
        product={rapidReloadProduct}
        savedPaymentMethodId={paymentMethods[0].id}
        loonCount={quickBuyLoonCount}
        onCancel={() => setDialog(null)}
        onSuccess={() => setDialog(null)}
        analyticsSurface="library"
      />
    );
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(nextOpen) => setDialog(nextOpen ? 'buy-loons' : null)}
    >
      <DialogContent className="max-w-md">
        {isResolvingQuickBuy ? (
          <>
            <DialogHeader>
              <DialogTitle>Buy Loons</DialogTitle>
              <DialogDescription>
                Checking your saved purchase settings.
              </DialogDescription>
            </DialogHeader>
            <div className="flex min-h-24 items-center justify-center">
              <Spinner className="size-5" />
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Buy Loons</DialogTitle>
              <DialogDescription>
                Top up your balance to play more games. See{' '}
                <span>
                  <a
                    href="/pricing"
                    className="cursor-pointer text-primary underline-offset-4 hover:underline"
                  >
                    pricing
                  </a>
                </span>{' '}
                for FAQs.
              </DialogDescription>
            </DialogHeader>
            <PricingTable
              layoutMode="singleColumn"
              analyticsSurface="library"
              onCancel={() => setDialog(null)}
              onPurchaseSuccess={() => setDialog(null)}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BuyLoonsDialog;
